<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useAccount } from 'dashboard/composables/useAccount';
import { billingWalletAPI } from 'dashboard/api/billingWallet';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const { checkPermissions } = usePolicy();

const wallet = ref(null);
const templates = ref([]);
const transactions = ref([]);
const meta = ref({ current_page: 1, total_pages: 1, count: 0 });
const isLoading = ref(true);
const isPurchaseSubmitting = ref(false);
const purchaseDialogRef = ref(null);
const pendingPurchase = ref(null);

const canPurchase = computed(() => checkPermissions(['administrator']));

const formatMoney = cents => {
  const n = Number(cents || 0);
  return (n / 100).toFixed(2);
};

const formatDateTime = iso => {
  if (!iso) return t('BILLING.APP.EMPTY_VALUE');
  return format(new Date(iso), 'yyyy-MM-dd HH:mm');
};

const purchaseConfirmDescription = computed(() => {
  const tpl = pendingPurchase.value;
  if (!tpl) return '';
  return t('BILLING.APP.PURCHASE_CONFIRM.DESCRIPTION', {
    name: tpl.name,
    months: tpl.duration_months,
    price: formatMoney(tpl.price_cents),
    unit: t('BILLING.APP.CURRENCY_UNIT'),
  });
});

const loadWallet = async () => {
  const { data } = await billingWalletAPI.show(accountId.value);
  wallet.value = data;
};

const loadTemplates = async () => {
  const { data } = await billingWalletAPI.packageTemplates(accountId.value);
  templates.value = data.payload || [];
};

const loadTransactions = async (page = 1) => {
  const { data } = await billingWalletAPI.transactions(accountId.value, {
    page,
    per_page: 25,
  });
  transactions.value = data.payload || [];
  meta.value = {
    current_page: data.meta?.current_page || 1,
    total_pages: data.meta?.total_pages || 1,
    count: data.meta?.count || 0,
  };
};

const loadAll = async () => {
  isLoading.value = true;
  try {
    await Promise.all([loadWallet(), loadTemplates(), loadTransactions(1)]);
  } catch (e) {
    useAlert(t('BILLING.APP.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const loadMore = async () => {
  if (meta.value.current_page >= meta.value.total_pages) return;
  const next = meta.value.current_page + 1;
  const { data } = await billingWalletAPI.transactions(accountId.value, {
    page: next,
    per_page: 25,
  });
  transactions.value = [...transactions.value, ...(data.payload || [])];
  meta.value = {
    current_page: data.meta?.current_page || next,
    total_pages: data.meta?.total_pages || meta.value.total_pages,
    count: data.meta?.count || meta.value.count,
  };
};

const openPurchaseConfirm = tpl => {
  if (!canPurchase.value) {
    useAlert(t('BILLING.APP.PURCHASE_FORBIDDEN'));
    return;
  }
  pendingPurchase.value = tpl;
  purchaseDialogRef.value?.open();
};

const onPurchaseDialogClose = () => {
  pendingPurchase.value = null;
};

const confirmPurchase = async () => {
  const tpl = pendingPurchase.value;
  if (!tpl || !canPurchase.value) return;

  isPurchaseSubmitting.value = true;
  try {
    await billingWalletAPI.purchase(accountId.value, tpl.id);
    useAlert(t('BILLING.APP.PURCHASE_SUCCESS'));
    await loadAll();
    purchaseDialogRef.value?.close();
    pendingPurchase.value = null;
  } catch (e) {
    const msg = e.response?.data?.message || e.message;
    useAlert(msg || t('BILLING.WALLET.INSUFFICIENT_BALANCE'));
  } finally {
    isPurchaseSubmitting.value = false;
  }
};

onMounted(() => {
  loadAll();
});
</script>

<template>
  <SettingsLayout :no-records-found="false" class="gap-10">
    <template #header>
      <BaseSettingsHeader
        :title="t('BILLING.APP.HEADER.TITLE')"
        :description="t('BILLING.APP.HEADER.DESCRIPTION')"
        feature-name="account-billing"
      />
    </template>

    <template #body>
      <div v-if="isLoading" class="py-12 text-center text-n-slate-11">
        {{ t('BILLING.APP.LOADING') }}
      </div>
      <div v-else class="flex flex-col gap-8 mt-2">
        <div class="grid gap-4 md:grid-cols-2">
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          >
            <p class="text-sm font-medium text-n-slate-11">
              {{ t('BILLING.APP.BALANCE_LABEL') }}
            </p>
            <p class="mt-2 text-2xl font-semibold text-n-slate-12 tabular-nums">
              {{ formatMoney(wallet?.balance_cents) }}
              <span class="text-base font-normal text-n-slate-11">{{
                t('BILLING.APP.CURRENCY_UNIT')
              }}</span>
            </p>
          </div>
          <div
            class="rounded-xl border border-n-weak bg-n-solid-1 p-5 shadow-sm"
          >
            <p class="text-sm font-medium text-n-slate-11">
              {{ t('BILLING.APP.PACKAGE_LABEL') }}
            </p>
            <p class="mt-2 text-lg font-semibold text-n-slate-12">
              {{
                wallet?.package_active
                  ? t('BILLING.APP.PACKAGE_ACTIVE')
                  : t('BILLING.APP.PACKAGE_INACTIVE')
              }}
            </p>
            <p
              v-if="wallet?.package_starts_at"
              class="mt-1 text-sm text-n-slate-11"
            >
              {{ t('BILLING.APP.STARTS_AT') }}:
              {{ formatDateTime(wallet.package_starts_at) }}
            </p>
            <p
              v-if="wallet?.package_ends_at"
              class="mt-1 text-sm text-n-slate-11"
            >
              {{ t('BILLING.APP.EXPIRES_AT') }}:
              {{ formatDateTime(wallet.package_ends_at) }}
            </p>
          </div>
        </div>

        <div>
          <h3 class="mb-3 text-base font-semibold text-n-slate-12">
            {{ t('BILLING.APP.PACKAGES_TITLE') }}
          </h3>
          <div
            v-if="!templates.length"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-6 text-sm text-n-slate-11"
          >
            {{ t('BILLING.APP.NO_PACKAGES') }}
          </div>
          <ul v-else class="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <li
              v-for="tpl in templates"
              :key="tpl.id"
              class="flex flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-4"
            >
              <div>
                <p class="font-semibold text-n-slate-12">{{ tpl.name }}</p>
                <p class="mt-1 text-sm text-n-slate-11">
                  {{
                    t('BILLING.APP.DURATION_MONTHS', { n: tpl.duration_months })
                  }}
                </p>
                <p class="mt-2 text-lg font-semibold text-n-slate-12 tabular-nums">
                  {{ formatMoney(tpl.price_cents) }}
                  {{ t('BILLING.APP.CURRENCY_UNIT') }}
                </p>
              </div>
              <p
                v-if="!tpl.purchasable"
                class="text-xs text-amber-11"
              >
                {{ t('BILLING.APP.NOT_PURCHASABLE') }}
              </p>
              <Button
                v-if="canPurchase && tpl.purchasable"
                size="sm"
                color="blue"
                class="self-start"
                @click="openPurchaseConfirm(tpl)"
              >
                {{ t('BILLING.APP.BUY') }}
              </Button>
            </li>
          </ul>
        </div>

        <div>
          <h3 class="mb-3 text-base font-semibold text-n-slate-12">
            {{ t('BILLING.APP.TRANSACTIONS_TITLE') }}
          </h3>
          <div
            v-if="!transactions.length"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-4 py-6 text-sm text-n-slate-11"
          >
            {{ t('BILLING.APP.TRANSACTIONS_EMPTY') }}
          </div>
          <div v-else class="overflow-x-auto rounded-xl border border-n-weak">
            <table class="w-full text-left text-sm">
              <thead class="bg-n-alpha-2 text-n-slate-11">
                <tr>
                  <th class="px-3 py-2 font-medium">
                    {{ t('BILLING.APP.COLUMNS.TIME') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('BILLING.APP.COLUMNS.TYPE') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('BILLING.APP.COLUMNS.DELTA') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('BILLING.APP.COLUMNS.BALANCE_AFTER') }}
                  </th>
                  <th class="px-3 py-2 font-medium">
                    {{ t('BILLING.APP.COLUMNS.DESCRIPTION') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="tx in transactions"
                  :key="tx.id"
                  class="border-t border-n-weak text-n-slate-12"
                >
                  <td class="px-3 py-2 whitespace-nowrap">
                    {{ formatDateTime(tx.created_at) }}
                  </td>
                  <td class="px-3 py-2">{{ tx.transaction_type_label }}</td>
                  <td class="px-3 py-2 tabular-nums">
                    {{ formatMoney(tx.delta_cents) }}
                  </td>
                  <td class="px-3 py-2 tabular-nums">
                    {{ formatMoney(tx.balance_after_cents) }}
                  </td>
                  <td class="px-3 py-2 max-w-xs truncate">
                    {{ tx.description || t('BILLING.APP.EMPTY_VALUE') }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            v-if="meta.current_page < meta.total_pages"
            class="mt-3 flex justify-center"
          >
            <Button size="sm" color="slate" @click="loadMore">
              {{ t('BILLING.APP.LOAD_MORE') }}
            </Button>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <Dialog
    ref="purchaseDialogRef"
    type="edit"
    width="md"
    :title="t('BILLING.APP.PURCHASE_CONFIRM.TITLE')"
    :description="purchaseConfirmDescription"
    :confirm-button-label="t('BILLING.APP.PURCHASE_CONFIRM.CONFIRM')"
    :cancel-button-label="t('BILLING.APP.PURCHASE_CONFIRM.CANCEL')"
    :is-loading="isPurchaseSubmitting"
    @confirm="confirmPurchase"
    @close="onPurchaseDialogClose"
  />
</template>
